import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, 
  AlertTriangle, Loader2, ListTodo, ClipboardList
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkUploadPage() {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('delegation');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const canAccessDelegation = user?.is_owner || user?.allowed_modules?.includes('delegation');
  const canAccessChecklists = user?.is_owner || user?.allowed_modules?.includes('checklists');

  const downloadTemplate = async (type) => {
    try {
      const endpoint = type === 'delegation' 
        ? `${API_URL}/bulk-upload/delegation-tasks/template`
        : `${API_URL}/bulk-upload/checklist-tasks/template`;
      
      const response = await axios.get(endpoint, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_tasks_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = type === 'delegation'
        ? `${API_URL}/bulk-upload/delegation-tasks`
        : `${API_URL}/bulk-upload/checklist-tasks`;
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} tasks imported successfully`);
      }
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} tasks failed to import`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setUploadResult({
        success_count: 0,
        failed_count: 0,
        errors: [error.response?.data?.detail || 'Upload failed']
      });
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const renderUploadCard = (type, title, description, IconComponent, color) => {
    const canAccess = type === 'delegation' ? canAccessDelegation : canAccessChecklists;
    
    if (!canAccess) {
      return (
        <Card className="opacity-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconComponent className={`w-5 h-5 ${color}`} />
              {title}
            </CardTitle>
            <CardDescription>You don't have access to this module</CardDescription>
          </CardHeader>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconComponent className={`w-5 h-5 ${color}`} />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1: Download Template */}
          <div className="p-4 border rounded-lg bg-muted/30">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div className="flex-1">
                <h4 className="font-medium">Download Template</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Download the Excel template, fill in your data, and save the file.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadTemplate(type)}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download {type === 'delegation' ? 'Delegation' : 'Checklist'} Template
                </Button>
              </div>
            </div>
          </div>

          {/* Step 2: Upload File */}
          <div className="p-4 border rounded-lg bg-muted/30">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-semibold text-sm">2</span>
              </div>
              <div className="flex-1">
                <h4 className="font-medium">Upload Your File</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Upload the filled Excel file to import tasks into the system.
                </p>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => handleFileUpload(e, type)}
                      className="hidden"
                      disabled={uploading}
                    />
                    <Button 
                      variant="default" 
                      size="sm"
                      className="gap-2"
                      disabled={uploading}
                      asChild
                    >
                      <span>
                        {uploading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        {uploading ? 'Uploading...' : 'Upload Excel File'}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Results */}
          {uploadResult && activeTab === type && (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                {uploadResult.success_count > 0 && (
                  <Badge variant="default" className="gap-1 bg-green-600">
                    <CheckCircle2 className="w-3 h-3" />
                    {uploadResult.success_count} Imported
                  </Badge>
                )}
                {uploadResult.failed_count > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="w-3 h-3" />
                    {uploadResult.failed_count} Failed
                  </Badge>
                )}
              </div>

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Import Errors</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside text-sm mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {uploadResult.errors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {uploadResult.success_count > 0 && uploadResult.failed_count === 0 && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Success!</AlertTitle>
                  <AlertDescription className="text-green-700">
                    All {uploadResult.success_count} tasks were imported successfully.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="bulk-upload-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-blue-600" />
            Bulk Upload Tasks
          </h1>
          <p className="text-muted-foreground mt-1">
            Import Delegation Tasks and Checklist Tasks from Excel files
          </p>
        </div>

        {/* Instructions */}
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>One-Time Migration Tool</AlertTitle>
          <AlertDescription>
            This tool is designed for migrating existing tasks from your old system. 
            Download the template, fill in your data following the format, and upload to import.
            Make sure to delete the sample rows before uploading.
          </AlertDescription>
        </Alert>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="delegation" className="gap-2" data-testid="tab-delegation">
              <ListTodo className="w-4 h-4" />
              Delegation Tasks
            </TabsTrigger>
            <TabsTrigger value="checklist" className="gap-2" data-testid="tab-checklist">
              <ClipboardList className="w-4 h-4" />
              Checklist Tasks
            </TabsTrigger>
          </TabsList>

          <TabsContent value="delegation" className="mt-4">
            {renderUploadCard(
              'delegation',
              'Delegation Tasks',
              'One-time tasks assigned to team members with specific due dates',
              ListTodo,
              'text-blue-600'
            )}
          </TabsContent>

          <TabsContent value="checklist" className="mt-4">
            {renderUploadCard(
              'checklist',
              'Checklist Tasks',
              'Recurring tasks with daily, weekly, fortnightly, monthly, quarterly, half-yearly, or yearly frequency',
              ClipboardList,
              'text-green-600'
            )}
          </TabsContent>
        </Tabs>

        {/* Format Guide */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Format Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <ListTodo className="w-4 h-4 text-blue-600" />
                  Delegation Tasks Columns
                </h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li><strong>Title*</strong> - Task name (required)</li>
                  <li><strong>Description</strong> - Detailed description</li>
                  <li><strong>Assigned To Email*</strong> - User email (required)</li>
                  <li><strong>Due Date*</strong> - YYYY-MM-DD format (required)</li>
                  <li><strong>Priority</strong> - normal or critical</li>
                  <li><strong>Status</strong> - pending, in_progress, completed</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-green-600" />
                  Checklist Tasks Columns
                </h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li><strong>Title*</strong> - Task name (required)</li>
                  <li><strong>Description</strong> - Detailed description</li>
                  <li><strong>Assigned To Email*</strong> - User email (required)</li>
                  <li><strong>Frequency*</strong> - daily, weekly, monthly, quarterly (required)</li>
                  <li><strong>Start Date</strong> - YYYY-MM-DD format</li>
                  <li><strong>Priority</strong> - normal or critical</li>
                  <li><strong>Status</strong> - pending, in_progress, completed</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
